import { z } from 'zod';
import { projectsDateSchema, projectsPriorityKeySchema, projectsProjectTypeKeySchema, projectsPropertyTypeKeySchema, } from '../contracts/projects-project.contract.js';
/** Portable capability identity; safe for manifests and browser consumers. */
export const projectsImportReconciliationCapability = 'projects.import-reconciliation.v1';
/**
 * Deterministic synthetic import mapping for the Projects-owned operational
 * fields of one original `projects` row. This seam maps and dispositions
 * values only; it performs no read, write, extraction, or original-data
 * access. A raw source value is either explicitly mapped by the authorized
 * tenant import profile, explicitly defaulted where the original column
 * carried the same default, omitted when the source is empty, or quarantined
 * WITH its raw evidence. Unknown values are never silently coerced to a
 * catch-all key, and the retired operating-entity selector is never stored,
 * displayed, aliased, or coerced into the target.
 */
export const projectsOperationalFieldImportContractVersion = 'projects.operational-field-import.v1';
/** Raw original operational column values as extracted text/booleans. */
export const projectsOperationalFieldImportSourceSchema = z.strictObject({
    type: z.string().max(200).nullable(),
    property_type: z.string().max(200).nullable(),
    priority: z.string().max(200).nullable(),
    business_entity: z.string().max(200).nullable(),
    is_water_job: z.boolean().nullable(),
    start_date: z.string().max(200).nullable(),
    target_completion_date: z.string().max(200).nullable(),
});
const importMappingRecord = (target) => z.record(z.string().min(1).max(200), target);
/**
 * The authorized tenant import profile for these fields. Mappings are
 * explicit: a source value absent from its mapping is quarantined, and every
 * mapped target key must exist in the tenant's configured key lists.
 */
export const projectsOperationalFieldImportProfileSchema = z.strictObject({
    canonicalSourceEntity: z.string().min(1).max(80).nullable(),
    typeMappings: importMappingRecord(projectsProjectTypeKeySchema),
    propertyTypeMappings: importMappingRecord(projectsPropertyTypeKeySchema),
    priorityMappings: importMappingRecord(projectsPriorityKeySchema),
    configuredProjectTypeKeys: z.array(projectsProjectTypeKeySchema).min(1).max(40),
    configuredPropertyTypeKeys: z.array(projectsPropertyTypeKeySchema).min(1).max(20),
    defaultProjectTypeKey: projectsProjectTypeKeySchema,
}).superRefine((profile, context) => {
    if (!profile.configuredProjectTypeKeys.includes(profile.defaultProjectTypeKey)) {
        context.addIssue({
            code: 'custom',
            path: ['defaultProjectTypeKey'],
            message: 'The default Project type key must be one of the configured Project type keys.',
        });
    }
});
export const projectsOperationalFieldImportRequestSchema = z.strictObject({
    contractVersion: z.literal(projectsOperationalFieldImportContractVersion),
    source: projectsOperationalFieldImportSourceSchema,
    profile: projectsOperationalFieldImportProfileSchema,
});
export const projectsOperationalFieldImportSourceFieldSchema = z.enum([
    'type',
    'property_type',
    'priority',
    'business_entity',
    'is_water_job',
    'start_date',
    'target_completion_date',
]);
export const projectsOperationalFieldImportOutcomeSchema = z.enum([
    'mapped',
    'defaulted',
    'omitted',
    'quarantined',
]);
export const projectsOperationalFieldImportQuarantineReasonSchema = z.enum([
    'unmapped-source-value',
    'unconfigured-target-key',
    'invalid-date',
    'date-range-invalid',
    'retired-entity-value',
]);
export const projectsOperationalFieldImportDispositionSchema = z.strictObject({
    field: projectsOperationalFieldImportSourceFieldSchema,
    outcome: projectsOperationalFieldImportOutcomeSchema,
    /** Exact raw source value retained as evidence; null when the source was null. */
    rawValue: z.union([z.string().max(200), z.boolean()]).nullable(),
    targetField: z.string().min(1).max(80).nullable(),
    targetValue: z.union([z.string().max(80), z.boolean()]).nullable(),
    quarantineReason: projectsOperationalFieldImportQuarantineReasonSchema.nullable(),
    explanation: z.string().min(1).max(500),
}).superRefine((row, context) => {
    if ((row.outcome === 'quarantined') !== (row.quarantineReason !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['quarantineReason'],
            message: 'Exactly the quarantined outcome carries a quarantine reason.',
        });
    }
    if ((row.outcome === 'mapped' || row.outcome === 'defaulted') && row.targetValue === null) {
        context.addIssue({
            code: 'custom',
            path: ['targetValue'],
            message: 'Mapped and defaulted outcomes carry a target value.',
        });
    }
});
export const projectsOperationalFieldImportResultSchema = z.strictObject({
    contractVersion: z.literal(projectsOperationalFieldImportContractVersion),
    dispositions: z.array(projectsOperationalFieldImportDispositionSchema).length(projectsOperationalFieldImportSourceFieldSchema.options.length),
    /** Target values ready for the Projects target schema; null clears/omits. */
    mappedFields: z.strictObject({
        projectType: projectsProjectTypeKeySchema,
        priority: projectsPriorityKeySchema,
        propertyType: projectsPropertyTypeKeySchema.nullable(),
        isWaterJob: z.boolean(),
        startDate: projectsDateSchema.nullable(),
        targetCompletionDate: projectsDateSchema.nullable(),
    }).nullable(),
    quarantined: z.boolean(),
});
//# sourceMappingURL=projects-operational-field-import.contract.js.map